import { useState, useEffect } from "react";
import { Zap, Activity, DollarSign, Download } from "lucide-react";
import axios, { API } from "@/utils/api";
import { useT } from "@/contexts/LanguageContext";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from "recharts";

const EnergyReportView = ({ clientId }) => {
  const t = useT();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  const handleDownloadPDF = async () => {
    setDownloading(true);
    try {
      const tokenRes = await axios.post(`${API}/clients/${clientId}/download-token`);
      const url = `${API}/clients/${clientId}/report/energy/pdf?token=${tokenRes.data.token}`;
      const a = document.createElement("a");
      a.href = url;
      a.setAttribute("download", "Energy_Report.pdf");
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (error) {
      console.error("Error downloading PDF:", error);
    } finally {
      setDownloading(false);
    }
  };

  useEffect(() => {
    const fetchReport = async () => {
      try {
        const response = await axios.get(`${API}/clients/${clientId}/report/energy`);
        setReport(response.data);
      } catch (error) {
        console.error("Error fetching energy report:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchReport();
  }, [clientId]);

  if (loading) return <div className="loading-container"><div className="spinner"></div></div>;

  const depts = report?.departments || [];

  return (
    <div data-testid="energy-report-view">
      <div className="page-header flex-between">
        <div>
          <h2 className="page-title">{t("energy_report_title")}</h2>
          <p className="page-subtitle">{t("energy_report_subtitle")}</p>
          <p className="text-muted" style={{ fontSize: "var(--text-small)" }}>{t("generated")}: {report?.report_generated_at ? new Date(report.report_generated_at).toLocaleString() : "N/A"}</p>
        </div>
        <button className="btn btn-primary" onClick={handleDownloadPDF} disabled={downloading} data-testid="download-energy-pdf">
          <Download size={18} /> {downloading ? "Downloading..." : `${t("download_report")} (PDF)`}
        </button>
      </div>

      {report?.client_info && (
        <div className="card mb-lg">
          <h4 className="card-title">{t("client_information")}</h4>
          <div className="form-row">
            <div><div className="text-muted" style={{ fontSize: "var(--text-small)" }}>{t("company")}</div><div>{report.client_info.company_name}</div></div>
            <div><div className="text-muted" style={{ fontSize: "var(--text-small)" }}>{t("contact")}</div><div>{report.client_info.contact_name}</div></div>
          </div>
        </div>
      )}

      <div className="stats-grid mb-lg">
        <div className="stat-card">
          <div className="stat-icon info"><Zap size={24} /></div>
          <div className="stat-value">{report?.total_energy_kwh?.toLocaleString()}</div>
          <div className="stat-label">{t("total_energy_kwh")}</div>
        </div>
        <div className="stat-card">
          <div className="stat-icon primary"><Activity size={24} /></div>
          <div className="stat-value">{report?.total_power_kw?.toLocaleString()}</div>
          <div className="stat-label">{t("total_power_kw")}</div>
        </div>
        <div className="stat-card">
          <div className="stat-icon warning"><DollarSign size={24} /></div>
          <div className="stat-value">${report?.total_cost?.toLocaleString()}</div>
          <div className="stat-label">{t("total_cost")}</div>
        </div>
      </div>

      <div className="card mb-lg">
        <h4 className="card-title">{t("energy_metrics")}</h4>
        <div className="table-container">
          <table className="table" data-testid="energy-report-table">
            <thead>
              <tr>
                <th>{t("department")}</th>
                <th>{t("inv_energy")}</th>
                <th>{t("inv_power")}</th>
                <th>{t("iot_energy")}</th>
                <th>{t("iot_power")}</th>
                <th>{t("iot_pf")}</th>
                <th>{t("audit_pct")}</th>
                <th>{t("status")}</th>
              </tr>
            </thead>
            <tbody>
              {depts.map((dept, idx) => (
                <tr key={idx}>
                  <td><strong>{dept.department}</strong></td>
                  <td>{dept.inventory_energy_kwh?.toLocaleString()}</td>
                  <td>{dept.inventory_power_kw?.toLocaleString()}</td>
                  <td>{dept.iot_energy_kwh?.toLocaleString()}</td>
                  <td>{dept.iot_power_kw?.toLocaleString()}</td>
                  <td>{dept.iot_power_factor ?? "-"}</td>
                  <td>{dept.audit_completion}%</td>
                  <td>
                    <span className={`badge badge-${dept.efficiency_rating === "Good" ? "success" : dept.efficiency_rating === "Needs Improvement" ? "warning" : "neutral"}`}>
                      {dept.efficiency_rating}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card mb-lg">
        <h4 className="card-title">{t("energy_comparison_chart")}</h4>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={depts.map(d => ({ name: d.department.split(" ")[0], inventory: d.inventory_energy_kwh, iot: d.iot_energy_kwh }))}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2EAE5" />
            <XAxis dataKey="name" tick={{ fill: "#526A5A", fontSize: 11 }} />
            <YAxis tick={{ fill: "#526A5A", fontSize: 11 }} />
            <Tooltip />
            <Legend />
            <Bar dataKey="inventory" fill="#244C36" name={t("inventory")} radius={[4, 4, 0, 0]} />
            <Bar dataKey="iot" fill="#3B82F6" name={t("iot_pmc")} radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="card">
        <h4 className="card-title">{t("recommendations")}</h4>
        <ul style={{ paddingLeft: "var(--space-lg)" }}>
          {report?.recommendations?.map((rec, i) => (
            <li key={i} style={{ marginBottom: "var(--space-sm)" }}>{rec}</li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default EnergyReportView;
